import { useParams, Link } from "react-router-dom";
import { BLOG_POSTS } from "./Blog";
import { ArrowLeft, Calendar, Tag } from "lucide-react";

export default function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);

    if (!post) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-20 text-center">
                <h1 className="text-3xl font-semibold">Article not found</h1>
                <Link to="/blog" className="text-[var(--lt-terracotta)] mt-4 inline-block">
                    Back to Blog
                </Link>
            </div>
        );
    }

    return (
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
            <Link
                to="/blog"
                className="text-sm text-[var(--lt-muted)] hover:text-[var(--lt-text)] inline-flex items-center gap-2"
            >
                <ArrowLeft size={14} /> Back to Blog
            </Link>

            <div className="flex items-center gap-3 text-xs mt-6">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--lt-terracotta)]/15 text-[var(--lt-terracotta)] font-semibold">
                    <Tag size={10} /> {post.category}
                </span>
                <span className="text-[var(--lt-muted)] inline-flex items-center gap-1">
                    <Calendar size={10} />{" "}
                    {new Date(post.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    })}
                </span>
                <span className="text-[var(--lt-muted)]">· {post.readingMinutes} min read</span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-4 leading-tight">
                {post.title}
            </h1>

            <div className="aspect-[16/9] w-full rounded-2xl overflow-hidden bg-[var(--lt-bg-2)] mt-8">
                <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                />
            </div>

            <div className="prose prose-invert max-w-none mt-8 text-[var(--lt-text)] leading-relaxed space-y-5 text-base">
                <p className="text-lg text-[var(--lt-muted)]">{post.excerpt}</p>
                {post.body ? (
                    post.body.map((block, i) => <Block key={i} block={block} />)
                ) : (
                    <p className="text-[var(--lt-muted)]" data-testid="blog-post-rewrite-note">
                        This article is being rewritten and will be back soon.
                    </p>
                )}
            </div>

        </article>
    );
}

function Block({ block }) {
    if (block.type === "h2") return <h2 className="text-xl font-semibold mt-8">{block.text}</h2>;
    if (block.type === "links") {
        return (
            <p>
                {block.text}{" "}
                {block.links.map((l, i) => (
                    <span key={l.to}>
                        {i > 0 && " · "}
                        <Link
                            to={l.to}
                            className="text-[var(--lt-terracotta)] font-semibold hover:underline"
                            data-testid={`blog-internal-link-${i}`}
                        >
                            {l.label}
                        </Link>
                    </span>
                ))}
            </p>
        );
    }
    if (block.type === "table") {
        return (
            <div className="overflow-x-auto rounded-xl border border-[var(--lt-border)]">
                <table className="w-full text-sm" data-testid="blog-conversion-table">
                    <thead className="bg-[var(--lt-bg-2)]">
                        <tr>
                            {block.head.map((h) => (
                                <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {block.rows.map((r) => (
                            <tr key={r[0]} className="border-t border-[var(--lt-border)]">
                                {r.map((c, i) => (
                                    <td key={i} className="px-4 py-3">{c}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    if (block.type === "example") {
        return (
            <div className="glass rounded-xl p-5 border-l-4 border-[var(--lt-terracotta)]" data-testid="blog-worked-example">
                <p className="font-semibold">{block.title}</p>
                <p className="mt-2">{block.text}</p>
            </div>
        );
    }
    return <p>{block.text}</p>;
}
